/**
 * Литеральные значения цветов для API, которые не понимают CSS-переменные:
 * Telegram WebApp (setHeaderColor, themeParams) и Leaflet (Polyline, divIcon).
 *
 * Значения дублируют токены из index.css. Источник истины — BRANDBOOK.md,
 * раздел 10. При изменении палитры править оба места.
 */

// Фон приложения — им красится хедер и подложка Telegram
export const TG_BG_LIGHT = '#FFFFFF';
export const TG_BG_DARK = '#0F1115';

// Текст
export const TG_TEXT_LIGHT = '#16181D';
export const TG_TEXT_DARK = '#F2F4F8';

// Подсказка
export const TG_HINT_LIGHT = '#68707C';
export const TG_HINT_DARK = '#8B95A6';

// Действие: синий. Светлая тема — brand-600, тёмная — осветлённый
export const ACTION_LIGHT = '#0072EF';
export const ACTION_DARK = '#3194FF';

// Текст на синей кнопке — белый в обеих темах (брендбук 2.3). В тёмной это
// 3.08 при норме 4.5: осознанное исключение, принятое в Этапе 5, — тёмная
// надпись читалась как отключённая. Прежний комментарий утверждал обратное
export const ON_ACTION_LIGHT = '#FFFFFF';
export const ON_ACTION_DARK = '#FFFFFF';

// Семантика — светлая / тёмная
export const SUCCESS_LIGHT = '#158043';
export const SUCCESS_DARK = '#3DD37F';
export const DANGER_LIGHT = '#DC2626';
export const DANGER_DARK = '#FF6B6B';
export const WARNING_LIGHT = '#B45309';
export const WARNING_DARK = '#FFB020';

// Нейтральные, нужны для маркеров и подложек на карте
export const NEUTRAL_HINT = '#68707C';
export const CARD_DARK = '#1C2028';
export const CARD_SECONDARY_DARK = '#232833';

// ── Карта (Leaflet) ──────────────────────────────────────────────────────────
// Leaflet рисует линии на canvas и собирает divIcon из строки HTML, поэтому
// var(--…) здесь не резолвится и нужны литералы. Значения — из шкалы бренда.

/** Проложенный маршрут: brand-500, читается на любой подложке карты */
export const MAP_ROUTE = '#007AFF';
/** Пройденный участок — успех, единственное зелёное на карте */
export const MAP_DONE = '#158043';
/** Точка пользователя. Отличается от линии маршрута, чтобы не сливаться */
export const MAP_USER = '#3395FF';
/** Точка вне радиуса */
export const MAP_FAR = '#DC2626';
/** Непройденная точка */
export const MAP_UPCOMING = '#232833';

/**
 * Карта всегда светлая — решение заказчика: на тёмных тайлах плохо читались
 * улицы и подписи. Значит и набор цветов маркеров нужен один, рассчитанный
 * под светлую подложку. Норма для графики — 3:1 (WCAG 1.4.11).
 */
const MAP_LIGHT = {
  route: MAP_ROUTE,
  // Пройденная точка — тот же синий, что и текущая: на экране один цвет
  // разной интенсивности, зелёный рядом читался как отдельное состояние
  done: MAP_ROUTE,
  // #3395FF на светлых тайлах — 2.68, ниже нормы. Тот же синий из шкалы,
  // но затемнённый до brand-700
  user: '#0453A9',
  far: MAP_FAR,
  upcoming: MAP_UPCOMING,
};

/** Набор цветов карты. Тайлы светлые в обеих темах, набор один */
export const mapPalette = () => MAP_LIGHT;

/**
 * Карта прогулки по маршруту редакции. Иерархия — как в Google Maps:
 * «иди сюда» — яркая сплошная с обводкой, «впереди» — точки того же
 * синего, «позади» — серая. Раньше всё было синим одного веса, и путь
 * к следующему месту не отличался от остального маршрута.
 *
 * Ведущая линия была тёмно-синей — её читали как «уже пройдено»:
 * тёмным многие приложения рисуют пройденный трек. Яркий синий —
 * цвет действия, «то, что делать сейчас» (брендбук 2.1).
 *
 * Контраст к светлой подложке (`#F2F0EC`), норма для графики 3:1:
 * guide/ahead 3.53 · passed 3.41 · рамка метки 5.39 · user 6.55
 */
export const WALK_MAP = {
  /** Путь до текущего места — главная линия экрана */
  guide: MAP_ROUTE,
  /** Обводка главной линии: отделяет её от белых улиц Positron */
  casing: '#FFFFFF',
  /** Маршрут впереди (точками) и пин текущего места */
  ahead: MAP_ROUTE,
  /** Пройденная часть маршрута и пройденные метки */
  passed: '#7A828F',
  /** Рамка и номер предстоящих меток */
  upcomingBorder: '#5B6270',
  upcomingText: '#16181D',
  /** Пользователь — тот же тёмный синий, что ведущая линия */
  user: '#0453A9',
};

/**
 * Основная подложка — векторный стиль Positron от OpenFreeMap: минимум
 * подписей, светлые кварталы, никаких значков магазинов. Векторный —
 * значит резкий на любом экране, в отличие от растра в 256px.
 *
 * Без ключа и без лимитов. Раньше здесь был CartoDB с тем же Positron,
 * но в 2025 CARTO закрыла бесплатный доступ и стала отдавать тайлы
 * с надписью «API KEY REQUIRED» во всю картинку.
 */
export const MAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';

/**
 * Запасная растровая подложка: показывается, пока едет maplibre, и если
 * OpenFreeMap недоступен. Стандартный стиль OSM цветной и подробный,
 * поэтому приглушаем CSS-фильтром — класс .map-tiles-muted в index.css.
 *
 * Ретины у OSM нет — плейсхолдер {r} не ставим, иначе будут 404.
 */
export const MAP_TILE_URL =
  'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

/**
 * Атрибуция обязательна: ODbL требует указания авторства данных.
 * Можно мелко и неброско, но не убирать
 */
export const MAP_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

/** Цвета Telegram-хедера и фона по теме */
export const tgChrome = (isDark) => (isDark ? TG_BG_DARK : TG_BG_LIGHT);
